import { useSignal } from '@preact/signals';

import { t } from '@amumin/i18n';
import { Button } from '@amumin/ui';
import { Avatar, Icon, weatherItemUrl, weatherSafetyUrl, weatherSymbolUrl } from '@amumin/ui-core';

import { api } from '../api.js';
import { LinkButton, Skeleton, useLoad } from '../lib/ui.js';
import { appText, household } from '../state/session.js';
import { familyLike, houseWords } from '../state/householdType.js';
import { joinItems, wp } from './text.js';
import { WeatherDetailDialog } from './WeatherDetail.js';
import styles from './weather.module.css';
import { householdPaused } from '../lib/paused.js';

// Ouderapp Vandaag: de weerkaart (DESIGN §8.WE.6). Voor ouders MET getallen (temperatuur,
// gevoel, wind in Beaufort, zonkracht "bij heldere lucht"), per moment de kinderpictogrammen als
// woorden, per kind met een afwijking een eigen regel, waarschuwingen met de letterlijke
// KNMI-tekst en "Bron: KNMI", en altijd "Weerdata: MET Norway". Zonder locatie: één keer de
// uitnodiging ("Niet nu" verbergt hem 30 dagen, alleen in deze browser).
// Golf 6b: tik op het weer (of "Meer over het weer") opent het detail van de dag, met daarin het
// verloop per uur (WeatherDetail.tsx).

const HIDE_KEY = 'fb.weather.invite.hiddenUntil';
const HIDE_MS = 30 * 24 * 3_600_000;

function inviteHidden(): boolean {
  try {
    const v = Number(window.localStorage.getItem(HIDE_KEY) ?? '0');
    return v > Date.now();
  } catch {
    return false;
  }
}

function hideInvite(): void {
  try {
    window.localStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_MS));
  } catch {
    // Geen opslag (privévenster): de uitnodiging komt dan gewoon terug.
  }
}

/** Vandaag (YYYY-MM-DD) in de tijdzone van het gezin; zonder tijdzone die van de browser. */
function todayKeyIn(timeZone: string | undefined): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

type WeatherToday = Awaited<ReturnType<typeof api.weather.today>>;

/**
 * Eén verzoek voor Vandaag én de weerkaart (binnen 5 seconden hetzelfde antwoord): Vandaag moet
 * weten of de uitnodiging er staat, om hem over de volle breedte te zetten in plaats van in een
 * smalle kolom van het raster.
 */
let inflight: { at: number; p: Promise<WeatherToday> } | null = null;
function weatherToday(): Promise<WeatherToday> {
  if (inflight && Date.now() - inflight.at < 5_000) return inflight.p;
  const p = api.weather.today();
  inflight = { at: Date.now(), p };
  p.catch(() => (inflight = null));
  return p;
}

/**
 * Is het weer nu één regel in plaats van een kaart? Dat is zo bij de eenmalige vraag "Wil je
 * weeradvies op het bord?" (geen locatie, niet weggeklikt, niet gepauzeerd) en als het weer even
 * niet bekend is (zonder waarschuwing). Vandaag zet die regel dan over de volle breedte bij de
 * andere meldingen, nooit in een smalle kolom van het raster.
 */
export function useWeatherLine(on: boolean): boolean {
  const load = useLoad(() => (on ? weatherToday() : Promise.resolve(null)), [on]);
  const r = load.data.value;
  if (!r) return false;
  if (!r.weather) {
    return r.reason === 'no_location' && !inviteHidden() && !householdPaused();
  }
  return r.weather.stale && r.weather.warnings.length === 0;
}

/**
 * Laatste antwoord per huishouden (fixer golf 6b r2): de kaart wordt opnieuw gemaakt bij Aanpassen
 * en bij elke verplaatsing. Zonder dit gaf hij tijdens het laden niets terug en zei de lege kaart
 * "Nu niets te zien", terwijl er gewoon weer is. Nu staat het vorige weer er meteen, en het nieuwe
 * vervangt het stil.
 */
let lastToday: { householdId: string; res: WeatherToday } | null = null;

interface MemberLite {
  id: string;
  display_name: string;
  color_key: string;
  avatar_icon: string | null;
}

export function WeatherCard({ members }: { members: ReadonlyArray<MemberLite> }) {
  const w = wp.value.weather;
  const load = useLoad(() => weatherToday());
  const hidden = useSignal(inviteHidden());
  const detail = useSignal(false);
  const hh = household.value?.id ?? '';
  const fresh = load.data.value;
  if (fresh) lastToday = { householdId: hh, res: fresh };
  const res = fresh ?? (lastToday && lastToday.householdId === hh ? lastToday.res : null);
  if (!res) {
    // Eerste keer laden: een rustig skelet in plaats van niets (of "Nu niets te zien").
    return load.loading.value ? (
      <section class="panel" data-name="weather-loading">
        <Skeleton rows={3} />
      </section>
    ) : null;
  }
  if (!res.weather) {
    // 0035 (review g4 ux r2): gepauzeerd = alleen kijken; geen "Locatie kiezen".
    if (res.reason !== 'no_location' || hidden.value || householdPaused()) return null;
    // UX-ronde 2: een eenmalige vraag als smalle banner; het kruisje is "Niet nu" (30 dagen).
    return (
      <section
        class="inline-banner"
        data-name="weather-invite"
        aria-labelledby="weather-invite-title"
      >
        <span class="inline-banner-icon" aria-hidden="true">
          <Icon id="cloud" size={18} />
        </span>
        <div class="inline-banner-text">
          <p class="inline-banner-title" id="weather-invite-title">
            {w.web.invite}
          </p>
          {/* Golf 6b: zonder kinderen geen kindertaal ("als de kinderen de deur uit gaan"). */}
          <p class="inline-banner-body">
            {familyLike.value ? w.web.inviteBody : houseWords.value.weatherBody}
          </p>
        </div>
        <div class="inline-banner-actions">
          <LinkButton
            href="/instellingen/locatie"
            variant="primary"
            size="small"
            icon="map-pin"
            name="weather-invite-choose"
          >
            {w.web.chooseLocation}
          </LinkButton>
        </div>
        <span class="inline-banner-close">
          <Button
            name="weather-invite-later"
            variant="ghost"
            icon="x"
            label={w.web.notNow}
            onPress={() => {
              hideInvite();
              hidden.value = true;
            }}
          />
        </span>
      </section>
    );
  }
  const wx = res.weather;
  const byId = new Map(members.map((x) => [x.id, x]));
  // Zonder titel heet een moment "Iedereen", tenzij hetzelfde moment in groepjes is gesplitst (een
  // kind dat het snel koud heeft krijgt eigen advies): dan de namen, anders klopt "Iedereen" niet.
  const momentLabel = (mo: (typeof wx.moments)[number]): string => {
    if (mo.title) return mo.title;
    const split = wx.moments.filter((o) => o.at_utc === mo.at_utc && !o.title).length > 1;
    if (!split) return w.web.everyone;
    const names = mo.member_ids
      .map((id) => byId.get(id)?.display_name)
      .filter((x): x is string => !!x);
    return names.length > 0 ? names.join(', ') : w.web.everyone;
  };
  const facts = wx.facts;
  // 's Avonds (vanaf 18:00) gaat de kaart over morgenochtend: dan heet hij ook zo, staan de
  // feiten onder "Morgen" en krijgt elk moment van morgen dat woord (review e r1).
  const today = todayKeyIn(household.value?.timezone);
  const first = wx.moments[0];
  const aboutTomorrow = !!first && first.date !== today;
  const title = aboutTomorrow
    ? wx.location_label
      ? t(w.web.tomorrowTitleAt, { label: wx.location_label })
      : w.web.tomorrowTitle
    : wx.location_label
      ? t(w.web.todayTitleAt, { label: wx.location_label })
      : w.web.todayTitle;
  const days = wx.stale ? [] : (wx.days ?? []);
  // Fixer golf 6b r2: dezelfde zonkracht als in het detail (het maximum van de dag), niet het
  // maximum van het ene moment; anders stond er "Zonkracht 1" op de kaart en "UV 3" eronder.
  const factsDay = first ? days.find((d) => d.date === first.date) : undefined;
  const uv =
    factsDay && factsDay.uv_max !== null
      ? { value: factsDay.uv_max, kind: factsDay.uv_kind }
      : facts && facts.max_uv !== null
        ? { value: Math.round(facts.max_uv), kind: facts.uv_kind }
        : null;
  const canOpen = days.length > 0;
  const openDetail = () => {
    if (canOpen) detail.value = true;
  };
  const d = appText.value.weatherDetail;
  // UX-ronde 2: weer even niet bekend (en geen waarschuwing) = één rustige regel, geen hele kaart.
  if (wx.stale && wx.warnings.length === 0) {
    return (
      <section
        class="status-line status-line-quiet"
        aria-labelledby="weather-card-title"
        data-name="weather-card"
        data-state="stale"
      >
        <span class="status-line-icon" aria-hidden="true">
          <Icon id="cloud-off" size={20} />
        </span>
        <p class="status-line-text">
          <strong id="weather-card-title">{title}</strong>
          <span class="status-line-sub" data-name="weather-stale">
            {w.stale}.
          </span>
        </p>
        <span class="status-line-actions">
          <a href="/instellingen/locatie#weer" data-name="weather-settings-link">
            {w.web.settings}
          </a>
        </span>
      </section>
    );
  }
  const itemsText = (codes: ReadonlyArray<string>) =>
    joinItems(
      codes.map((c) => (w.itemWords as Record<string, string>)[c] ?? c),
      w.and,
    );
  return (
    <section
      class={`panel ${styles.card}`}
      aria-labelledby="weather-card-title"
      data-name="weather-card"
    >
      <div class="section-head">
        <h2 class="section-title" id="weather-card-title">
          {title}
        </h2>
        {canOpen ? (
          <button
            type="button"
            class={`small ${styles.more}`}
            onClick={openDetail}
            data-name="weather-more"
          >
            {d.open}
            <Icon id="chevron-right" size={16} />
          </button>
        ) : null}
        <a href="/instellingen/locatie#weer" class="small" data-name="weather-settings-link">
          {w.web.settings}
        </a>
      </div>
      {wx.stale ? (
        <p class="muted" data-name="weather-stale">
          <strong>{w.stale}.</strong> {w.web.staleBody}
        </p>
      ) : (
        <>
          <button
            type="button"
            class={styles.now}
            data-name="weather-now"
            data-fit-open=""
            onClick={openDetail}
            disabled={!canOpen}
            aria-label={canOpen ? d.open : undefined}
          >
            {wx.now ? (
              <img
                src={weatherSymbolUrl(wx.now.symbol, wx.now.is_day)}
                width={48}
                height={48}
                alt=""
              />
            ) : null}
            <span class={styles.nowFacts}>
              {wx.now ? (
                <strong class={styles.temp}>
                  {aboutTomorrow ? `${w.web.nowLabel} ` : ''}
                  {t(w.web.tempNow, { temp: wx.now.temp_c })}
                </strong>
              ) : null}
              {facts ? (
                <span class="muted" data-name="weather-facts">
                  {aboutTomorrow ? `${w.web.tomorrowLabel}: ` : ''}
                  {t(w.feelsLike, { value: Math.round(facts.min_feels_c) })}
                  {' · '}
                  {t(w.wind, { bft: facts.beaufort })}
                  {uv
                    ? ` · ${t(uv.kind === 'clear_sky' ? w.uvClearSky : w.uvCloud, {
                        value: uv.value,
                      })}`
                    : ''}
                </span>
              ) : null}
            </span>
            {canOpen ? (
              <span class={styles.nowMore} aria-hidden="true">
                <Icon id="chevron-right" size={20} />
              </span>
            ) : null}
          </button>
          <ul class={styles.moments}>
            {wx.moments.map((mo, i) => (
              <li
                key={`${mo.at_utc}-${mo.member_ids.join(',')}`}
                class={styles.moment}
                data-name="weather-moment"
              >
                <span class={styles.momentTime}>
                  {mo.date !== today ? `${w.web.tomorrowLabel} ` : ''}
                  {mo.at_hhmm}
                </span>
                <span class={styles.momentTitle}>{momentLabel(mo)}</span>
                <span class={styles.momentWho}>
                  {mo.member_ids.slice(0, 4).map((id) => {
                    const x = byId.get(id);
                    return x ? (
                      <Avatar
                        key={id}
                        name={x.display_name}
                        colorKey={x.color_key}
                        icon={x.avatar_icon}
                        size="chip"
                      />
                    ) : null;
                  })}
                </span>
                <span class={styles.momentItems}>
                  {mo.items.map((c) => {
                    const url = weatherItemUrl(c);
                    return url ? <img key={c} src={url} width={28} height={28} alt="" /> : null;
                  })}
                  <span>{mo.text}</span>
                </span>
                {/* Voor ouders ook de rest van het advies (veiligheid, schaduw, opnieuw smeren). */}
                {(() => {
                  const extra = (wx.all_items[i] ?? []).filter(
                    (c) => mo.items.indexOf(c) < 0 && c !== 'warning',
                  );
                  return extra.length > 0 ? (
                    <span class={`muted small ${styles.momentExtra}`}>{itemsText(extra)}</span>
                  ) : null;
                })()}
              </li>
            ))}
          </ul>
        </>
      )}
      {wx.warnings.map((warn) => (
        <p
          key={`${warn.level}-${warn.phenomenon}`}
          class={`${styles.warn} ${warn.level === 'red' ? styles.warnRed : warn.level === 'orange' ? styles.warnOrange : styles.warnYellow}`}
          data-name="weather-warning"
          data-fit="keep"
        >
          <img src={weatherSafetyUrl(warn.phenomenon)} width={28} height={28} alt="" />
          <span>
            {/* Letterlijke brontekst; de kleurcode alleen ervoor als de bron hem niet al noemt. */}
            {/^code /i.test(warn.text_source) ? null : (
              <strong>{w.warningLevel[warn.level]}: </strong>
            )}
            {warn.text_source} <span class="muted">{warn.source_label}</span>
          </span>
        </p>
      ))}
      {/* De bron en een waarschuwing gaan nooit weg als de kaart op Vandaag klein is (cardFit). */}
      <p class={styles.credit} data-name="weather-card-attribution" data-fit="keep">
        {/* Uit de catalogus (review g3-ux r1: de server gaf alleen nl of en). */}
        <Icon id="info" size={14} /> {w.attribution || wx.attribution}
      </p>
      {canOpen ? (
        <WeatherDetailDialog
          open={detail.value}
          onClose={() => (detail.value = false)}
          days={days}
          initialDate={aboutTomorrow ? (first?.date ?? null) : today}
          todayKey={today}
          timeZone={household.value?.timezone}
          attribution={w.attribution || wx.attribution}
          locationLabel={wx.location_label}
        />
      ) : null}
    </section>
  );
}
